import { formatAmount } from '@mlm/shared/money';
import type { XlsxCellType, XlsxCellValue, XlsxColumn, XlsxSheet } from '@mlm/shared/xlsx';

/**
 * The one shape both export writers consume.
 *
 * A tab builds this once; `export-xlsx.ts` turns it into a workbook and
 * `export-pdf.ts` into a table PDF, so the two formats can never drift in
 * columns, ordering or totals. Everything here is pure and unit-tested — the
 * button component only dispatches.
 *
 * Imports use `@mlm/shared` SUBPATHS (`/money`, `/xlsx`) rather than the
 * barrel: the barrel re-exports ~46 modules including zod schemas and the
 * assistant, none of which belong in a page's eager module graph. For the same
 * reason this module is kept OUT of the `@mlm/ui` barrel — reach it as
 * `@mlm/ui/lib/export-table`.
 */
export type ExportColumn = XlsxColumn;
export type ExportRow = Record<string, XlsxCellValue>;

export type ExportTable = {
  /** Document heading, e.g. 'Statement of Account'. */
  title: string;
  /** Context lines under the heading: consignee, period, 'Amounts in PHP'… */
  subtitle: string[];
  /** Worksheet tab name (sanitized by the writer). */
  sheetName: string;
  columns: ExportColumn[];
  rows: ExportRow[];
  /** Bold in Excel, `<tfoot>` in the PDF. */
  totals?: ExportRow[];
  /** Filename without extension. */
  filenameBase: string;
};

/** Two-digit pad for the fixed date formats below. */
const p2 = (n: number) => String(n).padStart(2, '0');

/**
 * Fixed `yyyy-mm-dd` from LOCAL parts — deliberately not `toLocaleDateString`,
 * so exports are byte-stable across machines and test runs. Accepts the two
 * shapes the API actually emits: a real ISO string, and the SOA's Postgres
 * `::text` timestamp (`2026-08-26 14:05:00+08`).
 */
function parseLocal(value: string | number | Date): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);
  if (m) {
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] ?? 0),
      Number(m[5] ?? 0),
      Number(m[6] ?? 0),
    );
  }
  const parsed = new Date(value.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function localYmd(value: string | number | Date): string {
  const d = parseLocal(value);
  if (!d) return typeof value === 'string' ? value : '';
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

export function localYmdHm(value: string | number | Date): string {
  const d = parseLocal(value);
  if (!d) return typeof value === 'string' ? value : '';
  return `${localYmd(d)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/**
 * Render a cell as plain text (the PDF path; Excel gets typed values instead).
 *
 * Money goes through `formatAmount`, the sanctioned Decimal-based display
 * helper — it is the ONE place a monetary string becomes a number, and only
 * for display. No peso sign: jsPDF's built-in Helvetica has no glyph for it,
 * so every export's subtitle carries 'Amounts in PHP' instead.
 */
export function formatCell(value: XlsxCellValue, type: XlsxCellType): string {
  if (value === null || value === undefined || value === '') return '';
  switch (type) {
    case 'money':
      return formatAmount(typeof value === 'number' ? String(value) : String(value));
    case 'number':
      return String(value);
    case 'date':
      return localYmd(value as string | number | Date);
    case 'datetime':
      return localYmdHm(value as string | number | Date);
    default:
      return String(value);
  }
}

/**
 * Workbook layout: the data sheet keeps its headers on row 1 (so Excel's
 * sort/filter work and the repo's own reader can parse it back), and the
 * title/subtitle metadata goes on a second 'Info' sheet rather than as banner
 * rows above the table.
 */
export function tableToSheets(t: ExportTable): XlsxSheet[] {
  return [
    {
      name: t.sheetName,
      columns: t.columns,
      rows: t.rows,
      footerRows: t.totals,
    },
    {
      name: 'Info',
      columns: [
        { header: 'Field', key: 'field', type: 'string' },
        { header: 'Value', key: 'value', type: 'string' },
      ],
      rows: [
        { field: 'Report', value: t.title },
        ...t.subtitle.map((line, i) => ({ field: i === 0 ? 'Details' : '', value: line })),
      ],
      freezeHeader: false,
    },
  ];
}

/** Filename-safe slug of a person's name. */
export function slugify(name: string): string {
  const out = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  return out || 'consignee';
}

export function yyyymmdd(d: Date): string {
  return `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}`;
}

export function exportFilenameBase(
  consignee: string,
  kind: string,
  d: Date,
  suffix?: string,
): string {
  return `consignee-${slugify(consignee)}-${kind}-${suffix ?? yyyymmdd(d)}`;
}
